import { useState, useEffect, useRef } from 'react';
import {
  Container,
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  TextField,
  Toolbar,
  AppBar,
  Snackbar,
  Tooltip,
} from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import { PageName, FlagWithCount } from '../types';
import { DrawerNavigation, ConfirmDialog } from '../components';
import { DRAWER_WIDTH, COLLAPSED_WIDTH } from '../constants/ui';
import { listFlags, createFlag, renameFlag, deleteFlag, clearFlag } from '../services/flagService';

interface MMCFlagsProps {
  onLogout: () => void;
  onNavigate: (page: PageName, taskId?: string) => void;
}

// Truncate ID for display
const truncateId = (id: string): string => {
  if (id.length <= 12) return id;
  return `${id.slice(0, 8)}...${id.slice(-4)}`;
};

const MMCFlags: React.FC<MMCFlagsProps> = ({ onLogout, onNavigate }) => {
  // Data state
  const [flags, setFlags] = useState<FlagWithCount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const hasFetched = useRef(false);

  // Create dialog state
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newFlagLabel, setNewFlagLabel] = useState('');
  const [creating, setCreating] = useState(false);

  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingFlag, setEditingFlag] = useState<FlagWithCount | null>(null);
  const [editLabel, setEditLabel] = useState('');
  const [saving, setSaving] = useState(false);

  // Delete confirmation state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [flagToDelete, setFlagToDelete] = useState<FlagWithCount | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Clear confirmation state
  const [clearDialogOpen, setClearDialogOpen] = useState(false);
  const [flagToClear, setFlagToClear] = useState<FlagWithCount | null>(null);
  const [clearing, setClearing] = useState(false);

  // Snackbar state
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Drawer state
  const [drawerCollapsed, setDrawerCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const currentDrawerWidth = drawerCollapsed ? COLLAPSED_WIDTH : DRAWER_WIDTH;

  // Fetch flags on mount
  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;

    const fetchFlags = async () => {
      try {
        setLoading(true);
        const data = await listFlags('mmc');
        // Sort by count descending
        setFlags(data.sort((a, b) => b.count - a.count));
      } catch (err) {
        console.error('Failed to fetch flags:', err);
        setError('Failed to load flags');
      } finally {
        setLoading(false);
      }
    };

    fetchFlags();
  }, []);

  // Copy ID to clipboard
  const handleCopyId = async (id: string) => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(id);
      } else {
        // Fallback for non-HTTPS contexts
        const textArea = document.createElement('textarea');
        textArea.value = id;
        textArea.style.position = 'fixed';
        textArea.style.left = '-9999px';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      setSnackbar({ open: true, message: 'ID copied to clipboard', severity: 'success' });
    } catch (err) {
      console.error('Failed to copy:', err);
      setSnackbar({ open: true, message: 'Failed to copy ID', severity: 'error' });
    }
  };

  // Create flag
  const handleCreate = async () => {
    if (!newFlagLabel.trim()) return;
    setCreating(true);
    try {
      const result = await createFlag('mmc', newFlagLabel.trim());
      const newFlag: FlagWithCount = {
        id: result.id,
        label: newFlagLabel.trim(),
        count: 0,
      };
      setFlags((prev) => [...prev, newFlag].sort((a, b) => b.count - a.count));
      setCreateDialogOpen(false);
      setNewFlagLabel('');
      setSnackbar({ open: true, message: 'Flag created successfully', severity: 'success' });
    } catch (err) {
      console.error('Failed to create flag:', err);
      setSnackbar({ open: true, message: 'Failed to create flag', severity: 'error' });
    } finally {
      setCreating(false);
    }
  };

  // Open edit dialog
  const handleEditClick = (flag: FlagWithCount) => {
    setEditingFlag(flag);
    setEditLabel(flag.label);
    setEditDialogOpen(true);
  };

  // Save renamed flag
  const handleSaveRename = async () => {
    if (!editingFlag || !editLabel.trim()) return;
    setSaving(true);
    try {
      await renameFlag('mmc', editingFlag.id, editLabel.trim());
      setFlags((prev) =>
        prev.map((f) => (f.id === editingFlag.id ? { ...f, label: editLabel.trim() } : f))
      );
      setEditDialogOpen(false);
      setEditingFlag(null);
      setSnackbar({ open: true, message: 'Flag renamed successfully', severity: 'success' });
    } catch (err) {
      console.error('Failed to rename flag:', err);
      setSnackbar({ open: true, message: 'Failed to rename flag', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // Open delete confirmation
  const handleDeleteClick = (flag: FlagWithCount) => {
    setFlagToDelete(flag);
    setDeleteDialogOpen(true);
  };

  // Delete flag
  const handleConfirmDelete = async () => {
    if (!flagToDelete) return;
    setDeleting(true);
    try {
      await deleteFlag('mmc', flagToDelete.id);
      setFlags((prev) => prev.filter((f) => f.id !== flagToDelete.id));
      setDeleteDialogOpen(false);
      setFlagToDelete(null);
      setSnackbar({ open: true, message: 'Flag deleted successfully', severity: 'success' });
    } catch (err) {
      console.error('Failed to delete flag:', err);
      setSnackbar({ open: true, message: 'Failed to delete flag', severity: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  // Open clear confirmation
  const handleClearClick = (flag: FlagWithCount) => {
    setFlagToClear(flag);
    setClearDialogOpen(true);
  };

  // Clear flag from all images
  const handleConfirmClear = async () => {
    if (!flagToClear) return;
    setClearing(true);
    try {
      const result = await clearFlag('mmc', flagToClear.id);
      setFlags((prev) => prev.map((f) => (f.id === flagToClear.id ? { ...f, count: 0 } : f)));
      setClearDialogOpen(false);
      setFlagToClear(null);
      setSnackbar({
        open: true,
        message: `Cleared flag from ${result.cleared.toLocaleString()} images`,
        severity: 'success',
      });
    } catch (err) {
      console.error('Failed to clear flag:', err);
      setSnackbar({ open: true, message: 'Failed to clear flag', severity: 'error' });
    } finally {
      setClearing(false);
    }
  };

  // Calculate totals
  const totalFlags = flags.length;
  const totalImages = flags.reduce((sum, f) => sum + f.count, 0);

  return (
    <Box sx={{ display: 'flex' }}>
      {/* App Bar */}
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${currentDrawerWidth}px)` },
          ml: { md: `${currentDrawerWidth}px` },
          bgcolor: '#1976d2', // MMC blue
          transition: 'width 0.2s, margin 0.2s',
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            edge="start"
            onClick={() => setMobileOpen(true)}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <LocalOfferIcon sx={{ mr: 1 }} />
          <Typography variant="h6" noWrap sx={{ flexGrow: 1 }}>
            MMC Flags
          </Typography>
          <Button color="inherit" startIcon={<LogoutIcon />} onClick={onLogout}>
            Logout
          </Button>
        </Toolbar>
      </AppBar>

      {/* Drawer Navigation */}
      <DrawerNavigation
        currentPage="flagsMmc"
        onNavigate={onNavigate}
        collapsed={drawerCollapsed}
        onToggleCollapse={() => setDrawerCollapsed(!drawerCollapsed)}
        mobileOpen={mobileOpen}
        onMobileClose={() => setMobileOpen(false)}
      />

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { md: `calc(100% - ${currentDrawerWidth}px)` },
          ml: { md: `${currentDrawerWidth}px` },
          bgcolor: 'background.default',
          minHeight: '100vh',
          transition: 'width 0.2s, margin 0.2s',
        }}
      >
        <Toolbar />
        <Container maxWidth="lg">
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Summary */}
          <Paper sx={{ p: 2, mb: 3, display: 'flex', gap: 4, alignItems: 'center' }}>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Total Flags
              </Typography>
              <Typography variant="h5" fontWeight={600}>
                {totalFlags}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Total Images with Flags
              </Typography>
              <Typography variant="h5" fontWeight={600}>
                {totalImages.toLocaleString()}
              </Typography>
            </Box>
            <Box sx={{ flexGrow: 1 }} />
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCreateDialogOpen(true)}
              sx={{ bgcolor: '#1976d2' }}
            >
              Create Flag
            </Button>
          </Paper>

          {/* Flags Table */}
          <TableContainer component={Paper}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'action.hover' }}>
                    <TableCell sx={{ fontWeight: 600 }}>ID</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Flag Name</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">
                      Count
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="center">
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {flags.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">No flags found</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    flags.map((flag) => (
                      <TableRow key={flag.id} hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Tooltip title={flag.id} placement="top">
                              <Typography
                                variant="body2"
                                sx={{ fontFamily: 'monospace', color: 'text.secondary' }}
                              >
                                {truncateId(flag.id)}
                              </Typography>
                            </Tooltip>
                            <Tooltip title="Copy ID">
                              <IconButton
                                size="small"
                                onClick={() => handleCopyId(flag.id)}
                                sx={{ p: 0.5 }}
                              >
                                <ContentCopyIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography fontWeight={500}>{flag.label}</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography>{flag.count.toLocaleString()}</Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="Rename">
                            <IconButton size="small" onClick={() => handleEditClick(flag)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Clear from all images">
                            <IconButton
                              size="small"
                              color="warning"
                              onClick={() => handleClearClick(flag)}
                              disabled={flag.count === 0}
                            >
                              <ClearAllIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteClick(flag)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            )}
          </TableContainer>
        </Container>
      </Box>

      {/* Create Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Create New Flag</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Flag Label"
            value={newFlagLabel}
            onChange={(e) => setNewFlagLabel(e.target.value)}
            sx={{ mt: 1 }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && newFlagLabel.trim()) {
                handleCreate();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={!newFlagLabel.trim() || creating}
          >
            {creating ? 'Creating...' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Rename Flag</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Flag Label"
            value={editLabel}
            onChange={(e) => setEditLabel(e.target.value)}
            sx={{ mt: 1 }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && editLabel.trim()) {
                handleSaveRename();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveRename} disabled={!editLabel.trim() || saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleConfirmDelete}
        title={`Delete "${flagToDelete?.label}"?`}
        message={`This will remove the flag from all ${flagToDelete?.count.toLocaleString() || 0} images that have it. This action cannot be undone.`}
        confirmLabel="Delete"
        loading={deleting}
      />

      {/* Clear Confirmation Dialog */}
      <ConfirmDialog
        open={clearDialogOpen}
        onClose={() => setClearDialogOpen(false)}
        onConfirm={handleConfirmClear}
        title={`Clear "${flagToClear?.label}"?`}
        message={`This will remove the flag from all ${flagToClear?.count.toLocaleString() || 0} images. The flag will remain available for future use.`}
        confirmLabel="Clear"
        loading={clearing}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MMCFlags;
